import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';
import { GovernanceRulesInput, type GovernanceRules } from '@syndiqia/governance/contracts';
import { errorMessage, useApi } from '../api';
import { useDeliveries } from '../comms-queries';
import { BuildingForm, UnitForm } from '../components/RegistryForms';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, toNumber, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { Link } from 'react-router';
import { useBuildings, useUnits } from '../queries';
import { useMe, useSyndicate } from '../session';

/** One-time configuration of the syndicate: buildings and units. */
export function SettingsPage() {
  const { t } = useI18n();
  const s = useSyndicate();
  const buildings = useBuildings();
  const units = useUnits();

  if (!s.can('registry.configure')) return <Alert tone="info">{t.common.forbidden}</Alert>;
  return (
    <>
      <PageHeader title={t.settings.title} intro={t.settings.intro} />
      <dl className="facts panel">
        <dt>{t.settings.structure}</dt>
        <dd>
          {t.settings.structures[s.legalStructure]}
          <div className="muted small">{t.settings.structureHint}</div>
        </dd>
      </dl>
      {s.can('governance.read') && <MeetingRules />}
      {s.can('members.invite') && <AccessReview />}
      {s.can('members.roles') && s.access === 'member' && <SupportAccessPanel />}
      <h2>{t.settings.buildings}</h2>
      {buildings.isPending && <Loading label={t.loading} />}
      <ul className="cards">
        {buildings.data?.map((b) => (
          <li key={b.id} className="card card-body">
            <strong>{b.name}</strong>
            <span>{b.streetAddress}</span>
            <span className="muted">
              {b.city} ({b.province}) {b.postalCode}
            </span>
            <span className="badge">{t.home.units(units.data?.filter((u) => u.buildingId === b.id).length ?? 0)}</span>
            <Disclosure label={t.settings.addUnits}>{(close) => <UnitForm buildingId={b.id} onDone={close} />}</Disclosure>
          </li>
        ))}
      </ul>
      <p />
      <Disclosure label={t.buildings.add}>{(close) => <BuildingForm onDone={close} />}</Disclosure>
      {s.can('comms.log.read') && <SentEmails />}
    </>
  );
}

function SentEmails() {
  const { t, date } = useI18n();
  const deliveries = useDeliveries();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section>
      <h2>{t.emails.title}</h2>
      <p className="muted">{t.emails.intro}</p>
      {deliveries.data?.some((d) => d.provider === 'capture') && <Alert tone="info">{t.emails.capture}</Alert>}
      {deliveries.isPending && <Loading label={t.loading} />}
      {deliveries.data?.length === 0 && <Empty>{t.emails.empty}</Empty>}
      {!!deliveries.data?.length && (
        <table className="table">
          <thead>
            <tr>
              <th>{t.statement.date}</th>
              <th>{t.emails.to}</th>
              <th>{t.requests.subject}</th>
              <th>{t.requests.status}</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.data.map((d) => (
              <Fragment key={d.id}>
                <tr>
                  <td className="nowrap">{date(d.queuedAt.slice(0, 10))}</td>
                  <td>{d.address}</td>
                  <td>
                    <button type="button" className="link-button" onClick={() => setOpen(open === d.id ? null : d.id)}>
                      {d.subject}
                    </button>
                  </td>
                  <td>
                    <span className={`badge badge-delivery-${d.status}`}>{t.requests.deliveryStatus[d.status]}</span>
                  </td>
                </tr>
                {open === d.id && (
                  <tr className="row-detail">
                    <td colSpan={4}>
                      <pre className="email-body">{d.bodyText}</pre>
                      {d.lastError && <Alert>{d.lastError}</Alert>}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function MeetingRules() {
  const { t } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const queryClient = useQueryClient();
  const st = t.settings;
  const rules = useQuery({ queryKey: [s.tenantId, 'governance', 'rules'], queryFn: () => api<GovernanceRules>(`${s.api}/governance/rules`) });
  const [values, setValues] = useState<{
    voteBasis: string;
    quorumPercent: string;
    noticeMinDays: string;
    noticeMaxDays: string;
    boardQuorumPercent: string;
    boardCastingVote: boolean;
    boardNoticeDays: string;
  } | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMutation({
    mutationFn: (input: GovernanceRulesInput) => api<GovernanceRules>(`${s.api}/governance/rules`, { method: 'PUT', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'governance'] }),
  });
  if (!rules.data) return null;
  const r = rules.data;
  const v = values ?? {
    voteBasis: r.voteBasis,
    quorumPercent: String(r.quorumPercent),
    noticeMinDays: r.noticeMinDays?.toString() ?? '',
    noticeMaxDays: r.noticeMaxDays?.toString() ?? '',
    boardQuorumPercent: String(r.boardQuorumPercent),
    boardCastingVote: r.boardCastingVote,
    boardNoticeDays: r.boardNoticeDays?.toString() ?? '',
  };
  const set = (key: keyof typeof v) => (e: { target: { value: string } }) => setValues({ ...v, [key]: e.target.value });
  const editable = s.can('governance.approve');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(GovernanceRulesInput, {
      voteBasis: v.voteBasis,
      quorumPercent: toNumber(v.quorumPercent),
      noticeMinDays: v.noticeMinDays ? toNumber(v.noticeMinDays) : null,
      noticeMaxDays: v.noticeMaxDays ? toNumber(v.noticeMaxDays) : null,
      boardQuorumPercent: toNumber(v.boardQuorumPercent),
      boardCastingVote: v.boardCastingVote,
      boardNoticeDays: v.boardNoticeDays ? toNumber(v.boardNoticeDays) : null,
    });
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data);
  };
  return (
    <form className="panel form" onSubmit={submit} noValidate>
      <h2>{st.rulesTitle}</h2>
      <p className="muted small">{st.rulesIntro}</p>
      {r.isDefault && <Alert tone="info">{st.rulesDefault}</Alert>}
      <div className="row">
        <Field label={st.voteBasis}>
          <select value={v.voteBasis} onChange={set('voteBasis')} disabled={!editable}>
            {(['quote_parts', 'lots', 'persons'] as const).map((b) => (
              <option key={b} value={b}>
                {st.voteBases[b]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={st.quorumPercent} hint={st.quorumHint} error={errors.quorumPercent}>
          <input inputMode="decimal" value={v.quorumPercent} onChange={set('quorumPercent')} disabled={!editable} />
        </Field>
      </div>
      <div className="row">
        <Field label={st.noticeMin} hint={t.common.optional} error={errors.noticeMinDays}>
          <input inputMode="numeric" value={v.noticeMinDays} onChange={set('noticeMinDays')} disabled={!editable} />
        </Field>
        <Field label={st.noticeMax} hint={t.common.optional} error={errors.noticeMaxDays}>
          <input inputMode="numeric" value={v.noticeMaxDays} onChange={set('noticeMaxDays')} disabled={!editable} />
        </Field>
      </div>
      <h3>{st.boardRules}</h3>
      <div className="row">
        <Field label={st.boardQuorum} hint={st.boardQuorumHint} error={errors.boardQuorumPercent}>
          <input inputMode="decimal" value={v.boardQuorumPercent} onChange={set('boardQuorumPercent')} disabled={!editable} />
        </Field>
        <Field label={st.boardNotice} hint={t.common.optional} error={errors.boardNoticeDays}>
          <input inputMode="numeric" value={v.boardNoticeDays} onChange={set('boardNoticeDays')} disabled={!editable} />
        </Field>
      </div>
      <label className="checkbox">
        <input type="checkbox" checked={v.boardCastingVote} onChange={(e) => setValues({ ...v, boardCastingVote: e.target.checked })} disabled={!editable} /> {st.boardCasting}
      </label>
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      {save.isSuccess && !values && <Alert tone="info">{st.rulesSaved}</Alert>}
      {editable && (
        <div className="form-actions">
          <button type="submit" className="button button-primary" disabled={save.isPending}>
            {t.common.save}
          </button>
        </div>
      )}
    </form>
  );
}

interface AccessReviewData {
  accounts: { userId: string; name: string; email: string | null; roles: ('director' | 'manager' | 'owner' | 'occupant' | 'vendor')[]; lastSeenAt: string | null; invitedAt: string | null; party: { id: string; name: string } | null; flags: ('never_used' | 'no_record')[] }[];
  vendorLinks: { workOrderId: string; number: number; title: string; vendorName: string | null; expiresAt: string; lastUsedAt: string | null }[];
}

/** Access review (Law 25): who has access, what deserves a look, and removing access that is no longer needed. */
function AccessReview() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const queryClient = useQueryClient();
  const st = t.settings;
  const me = useMe();
  const review = useQuery({ queryKey: [s.tenantId, 'access', 'review'], queryFn: () => api<AccessReviewData>(`${s.api}/access/review`) });
  const remove = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => api(`${s.api}/access/${userId}/remove`, { method: 'POST', body: { role } }),
    onSuccess: () => Promise.all(['access', 'members'].map((k) => queryClient.invalidateQueries({ queryKey: [s.tenantId, k] }))),
  });
  if (!review.data) return review.isPending ? <Loading label={t.loading} /> : null;
  return (
    <section className="panel">
      <h2>{st.accessTitle}</h2>
      <p className="muted small">{st.accessIntro}</p>
      <ul className="access-review">
        {review.data.accounts.map((a) => {
          const removable = a.roles.filter((r) => r === 'owner' || r === 'occupant' || r === 'vendor');
          return (
            <li key={a.userId}>
              <div>
                {a.party ? <Link to={`${s.base}/parties/${a.party.id}`}>{a.name}</Link> : <strong>{a.name}</strong>}
                <span className="muted small"> · {a.roles.map((r) => t.roles[r]).join(', ')}</span>
                <div className="small muted">
                  {a.email ?? '—'} · {a.lastSeenAt ? st.lastSeen(date(a.lastSeenAt.slice(0, 10))) : st.neverSeen}
                </div>
                {a.flags.map((f) => (
                  <span key={f} className="badge badge-status-waiting">
                    {st.flags[f]}
                  </span>
                ))}
              </div>
              <div>
                {removable.length > 0 && a.userId !== me.data?.user.id ? (
                  <button type="button" className="link-button small" disabled={remove.isPending} onClick={() => window.confirm(st.confirmRemove(a.name)) && removable.forEach((role) => remove.mutate({ userId: a.userId, role }))}>
                    {st.removeAccess}
                  </button>
                ) : a.roles.some((r) => r === 'director' || r === 'manager') ? (
                  <span className="muted small">{st.boardRolesHint}</span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {remove.isError && <Alert>{errorMessage(remove.error, t)}</Alert>}
      <h3>{st.vendorLinks}</h3>
      {review.data.vendorLinks.length === 0 ? (
        <p className="muted small">{st.noVendorLinks}</p>
      ) : (
        <ul className="compact-list">
          {review.data.vendorLinks.map((l) => (
            <li key={l.workOrderId}>
              <Link to={`${s.base}/maintenance/work-orders/${l.workOrderId}`}>{st.vendorLink(l.number, l.title)}</Link>
              <span className="muted">{l.vendorName ?? '—'}</span>
              <span className="muted small">{date(l.expiresAt.slice(0, 10))}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface SupportAccessRow {
  id: string;
  staffName: string | null;
  staffEmail: string | null;
  reason: string;
  hours: number;
  status: 'requested' | 'approved' | 'rejected' | 'revoked' | 'expired';
  requestedAt: string;
  expiresAt: string | null;
  views: number;
  lastViewAt: string | null;
}

/** Directors decide on Syndiqia support's requests to see the client's data: temporary, read-only, logged. */
function SupportAccessPanel() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const queryClient = useQueryClient();
  const c = t.console;
  const list = useQuery({ queryKey: [s.tenantId, 'support-access'], queryFn: () => api<SupportAccessRow[]>(`${s.api}/support-access`) });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' | 'revoke' }) => api(`${s.api}/support-access/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'support-access'] }),
  });
  const when = (iso: string) => new Date(iso).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <section className="panel">
      <h2>{c.panelTitle}</h2>
      <p className="muted small">{c.panelIntro}</p>
      {list.data?.length === 0 && <p className="muted">{c.panelNone}</p>}
      <ul className="compact-list">
        {list.data?.map((a) => (
          <li key={a.id}>
            <span>
              <strong>{a.staffName ?? a.staffEmail}</strong> <span className="muted">· {a.reason}</span>
              <br />
              <span className="muted small">
                {c.panelAsked(date(a.requestedAt.slice(0, 10)), a.hours)}
                {a.expiresAt && a.status === 'approved' && ` · ${c.expires(when(a.expiresAt))}`}
                {a.views > 0 && ` · ${c.panelViews(a.views, a.lastViewAt ? when(a.lastViewAt) : '')}`}
              </span>
            </span>
            <span className="badge">{c.statuses[a.status]}</span>
            <span>
              {a.status === 'requested' && (
                <>
                  <button type="button" className="button button-small button-primary" disabled={act.isPending} onClick={() => window.confirm(c.confirmApprove(a.hours)) && act.mutate({ id: a.id, action: 'approve' })}>
                    {c.approve}
                  </button>{' '}
                  <button type="button" className="link-button small" disabled={act.isPending} onClick={() => act.mutate({ id: a.id, action: 'reject' })}>
                    {c.reject}
                  </button>
                </>
              )}
              {a.status === 'approved' && (
                <button type="button" className="link-button small" disabled={act.isPending} onClick={() => act.mutate({ id: a.id, action: 'revoke' })}>
                  {c.revoke}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {act.isError && <Alert>{errorMessage(act.error, t)}</Alert>}
    </section>
  );
}
